import type { FC } from "preact/compat";
import type { TabListProps } from "./interfaces";
import { Tab } from "@headlessui/react";
import { useEffect, useRef } from "preact/hooks";
import cn from "classnames";
import { useSignal } from "@preact/signals";

const TabList: FC<TabListProps> = ({ items, active, className }) => {
    const ref = useRef<HTMLDivElement | null>(null);
    const width = useSignal(0);
    const left = useSignal(0);

    useEffect(() => {
        if (!ref.current) {
            return;
        }

        const element = ref.current.querySelector<HTMLElement>(
            `[data-tab-index="${active}"]`
        );

        if (!element) {
            return;
        }

        const rect = element.getBoundingClientRect();

        width.value = rect.width;
        left.value = rect.left - ref.current.getBoundingClientRect().left;
    }, [active, left, width]);

    return (
        <div ref={ref} className={cn("flex relative", className)}>
            <div
                style={{
                    transform: `translateX(${left}px)`,
                    width: `${width}px`,
                }}
                className="absolute transition-all duration-100 h-full rounded-[20px] bg-sky-800 shadow-inner-bold origin-left"
            />
            <Tab.List className="flex rounded-[20px] border border-gray-200 relative">
                {items.map((tab, index) => (
                    <Tab
                        key={tab}
                        data-tab-index={index}
                        className={({ selected }) =>
                            cn(
                                "px-6 py-1 border-y border-transparent capitalize transition duration-100 outline-none",
                                selected ? "text-white" : "text-gray-400"
                            )
                        }
                    >
                        {tab}
                    </Tab>
                ))}
            </Tab.List>
        </div>
    );
};

export default TabList;
